/* freight_quote.js — MGS Freight Quote Form (Phase 1)
 * Production version: validation, F3 checkbox hide, in-hours/after-hours
 * promise text, honeypot bot protection, conservative email-domain check.
 */
(function () {
  'use strict';

  var SUBMIT_DEDUPE_MS = 60 * 1000;
  var lastSubmitKey = null;
  var lastSubmitAt = 0;
  var cachedAck = null;
  var skipUsed = false;
  var hideAttempts = 0;
  var MAX_HIDE_ATTEMPTS = 60;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function injectStyle() {
    if (document.getElementById('mgs-fq-style')) return;
    var style = document.createElement('style');
    style.id = 'mgs-fq-style';
    style.textContent = '.mgs-fq-ack-hidden { display: none !important; }';
    (document.head || document.documentElement).appendChild(style);
  }

  function getNextOpenLabel() {
    var p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false, weekday: 'short' })
      .formatToParts(new Date()).reduce(function (a, x) { a[x.type] = x.value; return a; }, {});
    var hour = parseInt(p.hour, 10);
    var dow  = p.weekday;
    var weekday = ['Mon','Tue','Wed','Thu','Fri'].indexOf(dow) > -1;
    var inHours = weekday && hour >= 9 && hour < 18;
    if (inHours) return null;
    if (dow === 'Sat' || dow === 'Sun' || (dow === 'Fri' && hour >= 18)) return 'by 10 AM EST Monday';
    if (weekday && hour < 9) return 'today by 10 AM EST';
    return 'tomorrow by 10 AM EST';
  }

  function updatePromiseText() {
    var label = getNextOpenLabel();
    if (!label) return;
    var fullEls = document.querySelectorAll('.mgs-fq-promise .mgs-fq-text-full');
    var shortEls = document.querySelectorAll('.mgs-fq-promise .mgs-fq-text-short');
    for (var i = 0; i < fullEls.length; i++) fullEls[i].textContent = "We'll email your exact shipping cost " + label + ".";
    for (var j = 0; j < shortEls.length; j++) shortEls[j].textContent = "Exact shipping cost " + label + ".";
  }

  function readField(name, root) {
    var els = $all('[data-mgs-fq-field="' + name + '"]', root);
    if (!els.length) return '';
    return (els[0].value || '').trim();
  }

  function readProductContext() {
    // Try several selector patterns; themes vary
    var variantInput =
      $('form[action*="/cart/add"] input[name="id"]') ||
      $('form[action*="cart/add"] input[name="id"]') ||
      $('product-form input[name="id"]') ||
      $('input[name="id"][type="hidden"]');

    var variantId = (variantInput && variantInput.value) ? variantInput.value : null;

    // URL fallback — Shopify writes ?variant=<id> when the customer picks a variant
    if (!variantId) {
      try {
        var params = new URLSearchParams(window.location.search);
        var v = params.get('variant');
        if (v) variantId = String(v);
      } catch (_) {}
    }

    var product = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) || {};
    var variants = product.variants || [];
    var matched = variants.filter(function (v) { return String(v.id) === String(variantId); })[0] || variants[0] || {};

    // Final fallback — first variant if nothing else gave us anything
    if (!variantId && matched.id) {
      variantId = String(matched.id);
    }

    return {
      variant_id: variantId,
      sku: matched.sku || null,
      title: product.title || document.title || '',
      vendor: product.vendor || null,
      price_cents: matched.price || null,
      page_url: window.location.href
    };
  }

  function readFreightInputs(root) {
    return {
      email:        readField('email', root),
      delivery_zip: readField('delivery_zip', root),
      phone:        readField('phone', root),
      notes:        readField('notes', root),
      quantity:     readField('quantity', root)
    };
  }

  function deriveName(email) {
    var local = (email.split('@')[0] || '').replace(/[._\-+]+/g, ' ').trim();
    var parts = local.split(/\s+/);
    return { first_name: parts[0] || 'Quote', last_name: parts.slice(1).join(' ') || 'Lead' };
  }

  function validate(freight) {
    var errors = [];
    if (!freight.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(freight.email)) errors.push('Enter a valid email address.');
    if (!/^\d{5}$/.test(freight.delivery_zip)) errors.push('Enter a 5-digit ZIP code.');
    if (freight.phone && freight.phone.replace(/\D/g, '').length < 10) errors.push('Enter a valid phone number, or leave it blank.');
    return errors;
  }

  /* Conservative email-domain check via DNS-over-HTTPS.
   * Returns false ONLY when the domain provably cannot receive mail
   * (no MX records AND no A records, with both queries returning conclusive
   * DNS responses — NOERROR with empty answers, or NXDOMAIN).
   * Returns true on: any record found, any non-conclusive DNS status,
   * any network/timeout failure. Fail-open is the default.
   */
  function hasValidEmailDomain(email) {
    var domain = ((email || '').split('@')[1] || '').trim().toLowerCase();
    if (!domain || domain.indexOf('.') === -1) return Promise.resolve(true);

    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(function () { try { controller.abort(); } catch (_) {} }, 1500) : null;
    var opts = { headers: { 'Accept': 'application/dns-json' } };
    if (controller) opts.signal = controller.signal;

    var mxUrl = 'https://dns.google/resolve?name=' + encodeURIComponent(domain) + '&type=MX';
    var aUrl  = 'https://dns.google/resolve?name=' + encodeURIComponent(domain) + '&type=A';

    return Promise.all([fetch(mxUrl, opts), fetch(aUrl, opts)])
      .then(function (responses) { return Promise.all([responses[0].json(), responses[1].json()]); })
      .then(function (results) {
        if (timeoutId) clearTimeout(timeoutId);
        var mxData = results[0] || {};
        var aData  = results[1] || {};

        // Any positive answer on either record type → mail is deliverable per RFC 5321
        var hasMx = mxData.Status === 0 && Array.isArray(mxData.Answer) && mxData.Answer.length > 0;
        var hasA  = aData.Status  === 0 && Array.isArray(aData.Answer)  && aData.Answer.length  > 0;
        if (hasMx || hasA) return true;

        // Only block if BOTH lookups returned a conclusive "nothing here" response.
        // Conclusive = Status 0 (NOERROR, with empty Answer) or Status 3 (NXDOMAIN).
        // Anything else (SERVFAIL=2, FORMERR=1, REFUSED=5, etc.) = uncertain → allow.
        var mxConclusive = (mxData.Status === 0 || mxData.Status === 3);
        var aConclusive  = (aData.Status  === 0 || aData.Status  === 3);
        if (!mxConclusive || !aConclusive) return true;

        // Both queries conclusively returned no records — domain cannot receive mail.
        return false;
      })
      .catch(function (err) {
        if (timeoutId) clearTimeout(timeoutId);
        console.warn('[mgs-fq] DNS check failed, allowing submission:', err);
        return true; // fail-open
      });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;';
    });
  }

  function showError(root, message) {
    if (!root) return;
    var box = $('[data-mgs-fq-error]', root);
    if (!box) return;
    box.innerHTML = '<strong>' + escapeHtml(message) + '</strong>';
    box.hidden = false;
  }
  function clearError(root) {
    if (!root) return;
    var box = $('[data-mgs-fq-error]', root);
    if (box) box.hidden = true;
  }

  function showConfirmation(root) {
    if (!root) return;
    var ctaRow = $('.mgs-fq-cta-row', root);
    var skip = $('[data-mgs-fq-skip]', root);
    var grid = $('.mgs-fq-grid', root);
    var promise = $('.mgs-fq-promise', root);
    var confirmEl = $('[data-mgs-fq-confirm]', root);
    if (ctaRow) ctaRow.style.display = 'none';
    if (skip) skip.style.display = 'none';
    if (grid) grid.style.display = 'none';
    if (promise) promise.style.display = 'none';
    if (confirmEl) confirmEl.hidden = false;
  }

  function buildPayload(root) {
    var product = readProductContext();
    var freight = readFreightInputs(root);
    var name = deriveName(freight.email);
    var quantity = Math.max(1, Math.min(99, parseInt(freight.quantity, 10) || 1));
    return {
      product: {
        title: product.title,
        sku: product.sku,
        variant_id: product.variant_id,
        vendor: product.vendor,
        price_cents: product.price_cents,
        quantity: quantity
      },
      customer: {
        first_name: name.first_name,
        last_name: name.last_name,
        email: freight.email,
        phone: freight.phone
      },
      freight: {
        delivery_zip: freight.delivery_zip,
        notes: freight.notes
      },
      submitted_at: new Date().toISOString(),
      page_url: product.page_url,
      source: window.MGSSource || null,
      _form_version: 'phase1-src'
    };
  }

  function submit(root) {
    if (!root) { console.error('[mgs-fq] submit called with no root'); return; }

    // Honeypot — global query so DOM rearrangement can't bypass it
    var honeypotEl = document.querySelector('[data-mgs-fq-honeypot]');
    if (honeypotEl && honeypotEl.value) {
      console.log('[mgs-fq] honeypot tripped, silently dropping submission');
      showConfirmation(root);
      return;
    }

    clearError(root);
    var freight = readFreightInputs(root);
    var errors = validate(freight);
    if (errors.length) { showError(root, errors[0]); return; }

    var WEBHOOK_URL = window.FREIGHT_QUOTE_WEBHOOK_URL;
    if (!WEBHOOK_URL) {
      console.error('[mgs-fq] FREIGHT_QUOTE_WEBHOOK_URL not configured');
      showError(root, 'Configuration error. Please call 1-307-381-1810.');
      return;
    }

    var btn = $('[data-mgs-fq-submit]', root);
    var originalHtml = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }

    hasValidEmailDomain(freight.email).then(function (domainOk) {
      if (!domainOk) {
        if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
        showError(root, "Please double-check your email — we couldn't deliver to that domain. Or call us at 1-307-381-1810.");
        return;
      }

      var payload = buildPayload(root);
      var dedupeKey = (payload.customer.email + '|' + payload.product.sku).toLowerCase();
      var now = Date.now();
      if (dedupeKey === lastSubmitKey && (now - lastSubmitAt) < SUBMIT_DEDUPE_MS) {
        if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
        showConfirmation(root);
        return;
      }

      if (btn) { btn.textContent = 'Sending…'; }

      fetch(WEBHOOK_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true })
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json().catch(function () { return {}; }); })
        .then(function () { lastSubmitKey = dedupeKey; lastSubmitAt = now; showConfirmation(root); })
        .catch(function (err) {
          console.error('[mgs-fq] webhook error', err);
          showError(root, "Couldn't send your request. Please try again or call us at 1-307-381-1810.");
          if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
        });
    });
  }

  function findAck() {
    var nameSelectors = [
      'input[type="checkbox"][name^="properties[Shipping cost required"]',
      'input[type="checkbox"][name*="Shipping cost"]',
      'input[type="checkbox"][name*="Plus Shipping"]',
      'input[type="checkbox"][name*="shipping cost"]'
    ];
    var input = null;
    for (var i = 0; i < nameSelectors.length && !input; i++) input = document.querySelector(nameSelectors[i]);
    if (!input) {
      var checkboxes = $all('input[type="checkbox"]');
      for (var j = 0; j < checkboxes.length; j++) {
        var labelEl = checkboxes[j].closest('label') || checkboxes[j].parentElement;
        var text = (labelEl && labelEl.textContent || '').toLowerCase();
        if (text.indexOf('plus shipping') !== -1 || text.indexOf('contacted w/ shipping') !== -1 || text.indexOf('shipping cost') !== -1) {
          input = checkboxes[j]; break;
        }
      }
    }
    if (!input) return null;
    var wrappingLabel = input.closest('label');
    var separateLabel = null;
    if (input.id) {
      try { separateLabel = document.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]'); } catch (_) {}
    }
    return { input: input, wrappingLabel: wrappingLabel, separateLabel: separateLabel };
  }

  function isAckHidden(ack) {
    if (!ack) return false;
    if (ack.wrappingLabel && ack.wrappingLabel.classList.contains('mgs-fq-ack-hidden')) return true;
    if (ack.input && ack.input.classList.contains('mgs-fq-ack-hidden')) return true;
    return false;
  }

  function hideAck(ack) {
    if (!ack) return;
    if (ack.wrappingLabel) {
      ack.wrappingLabel.classList.add('mgs-fq-ack-hidden');
    } else {
      if (ack.input) ack.input.classList.add('mgs-fq-ack-hidden');
      if (ack.separateLabel) ack.separateLabel.classList.add('mgs-fq-ack-hidden');
    }
  }

  function revealAndCheckAck(ack) {
    if (!ack) return;
    if (ack.wrappingLabel) ack.wrappingLabel.classList.remove('mgs-fq-ack-hidden');
    if (ack.input) ack.input.classList.remove('mgs-fq-ack-hidden');
    if (ack.separateLabel) ack.separateLabel.classList.remove('mgs-fq-ack-hidden');
    if (ack.input && !ack.input.checked) {
      ack.input.checked = true;
      ack.input.dispatchEvent(new Event('change', { bubbles: true }));
      ack.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function tryHideCheckbox() {
    if (!document.querySelector('[data-mgs-fq-root]')) return false;
    if (skipUsed) return true;
    if (cachedAck && isAckHidden(cachedAck)) return true;
    if (hideAttempts++ > MAX_HIDE_ATTEMPTS) return false;
    var ack = findAck();
    if (ack) {
      cachedAck = ack;
      hideAck(ack);
      armReHideObserver();
      return true;
    }
    return false;
  }

  function armReHideObserver() {
    if (!cachedAck || !window.MutationObserver) return;
    var els = [cachedAck.wrappingLabel, cachedAck.separateLabel, cachedAck.input].filter(Boolean);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.mgsFqObserved === '1') continue;
      el.dataset.mgsFqObserved = '1';
      var obs = new MutationObserver(function () {
        if (skipUsed) return;
        if (!isAckHidden(cachedAck)) hideAck(cachedAck);
      });
      obs.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
    }
  }

  function showSkipNote(root) {
    if (!root || root.querySelector('.mgs-fq-skip-note')) return;
    var note = document.createElement('div');
    note.className = 'mgs-fq-skip-note';
    note.textContent = "Got it — add this to your cart below and we'll email you about shipping costs after.";
    note.style.cssText = 'margin: 8px 0 12px; padding: 10px 12px; background: #ecfdf5; border-left: 3px solid #10b981; color: #065f46; font-size: 14px; line-height: 1.4; border-radius: 4px;';
    root.appendChild(note);
  }

  function handleSkip(root) {
    if (!root) return;
    skipUsed = true;
    if (!cachedAck) cachedAck = findAck();
    if (cachedAck) revealAndCheckAck(cachedAck);

    var grid = $('.mgs-fq-grid', root);
    var ctaRow = $('.mgs-fq-cta-row', root);
    var promise = $('.mgs-fq-promise', root);
    var skipLink = $('[data-mgs-fq-skip]', root);
    var errorBox = $('[data-mgs-fq-error]', root);
    if (grid) grid.style.display = 'none';
    if (ctaRow) ctaRow.style.display = 'none';
    if (promise) promise.style.display = 'none';
    if (skipLink) skipLink.style.display = 'none';
    if (errorBox) errorBox.hidden = true;

    showSkipNote(root);

    var addToCartBtn = $('form[action*="/cart/add"] [type="submit"]');
    var target = addToCartBtn || (cachedAck && (cachedAck.wrappingLabel || cachedAck.input));
    if (target && target.scrollIntoView) {
      try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
    }
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Quantity stepper +/− buttons
    var qtyBtn = t.closest('[data-mgs-fq-qty-action]');
    if (qtyBtn) {
      e.preventDefault();
      var root = qtyBtn.closest('[data-mgs-fq-root]');
      var input = root && root.querySelector('input[data-mgs-fq-field="quantity"]');
      if (input) {
        var current = parseInt(input.value, 10) || 1;
        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || 99;
        var action = qtyBtn.getAttribute('data-mgs-fq-qty-action');
        var next = current;
        if (action === 'increment') next = Math.min(max, current + 1);
        else if (action === 'decrement') next = Math.max(min, current - 1);
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var s = t.closest('[data-mgs-fq-submit]');
    if (s) { e.preventDefault(); submit(s.closest('[data-mgs-fq-root]')); return; }
    var k = t.closest('[data-mgs-fq-skip]');
    if (k) { e.preventDefault(); handleSkip(k.closest('[data-mgs-fq-root]')); }
  });

  function startup() {
    injectStyle();
    updatePromiseText();
    if (tryHideCheckbox()) return;
    var interval = setInterval(function () {
      if (tryHideCheckbox() || hideAttempts > MAX_HIDE_ATTEMPTS) clearInterval(interval);
    }, 200);
    if (window.MutationObserver) {
      var obs = new MutationObserver(function () { tryHideCheckbox(); });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { try { obs.disconnect(); } catch (_) {} }, 15000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startup);
  } else {
    startup();
  }

  console.log('[mgs-fq] script loaded, delegation active');
})();